import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// withCredentials: needed so the session_token HttpOnly cookie is sent on every request.
export const api = axios.create({ baseURL: API, withCredentials: true });

// ---------- Resilience: retry on gateway/network errors ----------
// Production deploys (and rare cold-starts) briefly return 502/503/504 from the
// Kubernetes ingress while the new backend pod warms up. Auto-retry GET requests
// with exponential backoff so end users never see a transient blank page.

const RETRYABLE_STATUSES = new Set([502, 503, 504]);
const MAX_RETRIES = 3;
const BASE_DELAY_MS = 800; // 0.8s, 1.6s, 3.2s
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// Lightweight pub/sub so a global banner can show "Reconnecting…" without
// dragging in a state library. UI listens via `subscribeApiStatus`.
let _retrying = false;
const _listeners = new Set();
function setRetrying(v) {
  if (_retrying === v) return;
  _retrying = v;
  for (const fn of _listeners) {
    try {
      fn(_retrying);
    } catch (err) {
      // A subscriber threw — log but keep iterating so other subscribers still get the update.
      console.warn("[api] retrying subscriber failed:", err);
    }
  }
}
export function subscribeApiStatus(fn) {
  _listeners.add(fn);
  fn(_retrying);
  return () => _listeners.delete(fn);
}

function isRetryable(error) {
  // Only retry idempotent methods (GET) to avoid duplicate writes.
  const method = (error.config?.method || "get").toLowerCase();
  if (method !== "get") return false;
  // Network error (no response) — retry.
  if (!error.response) return true;
  // Gateway statuses — retry.
  return RETRYABLE_STATUSES.has(error.response.status);
}

api.interceptors.response.use(
  (res) => {
    setRetrying(false);
    return res;
  },
  async (error) => {
    // On 401 anywhere except the /auth/* probes, bounce to /login.
    if (error.response?.status === 401) {
      setRetrying(false);
      const path = (error.config?.url || "").toString();
      const onLogin = typeof window !== "undefined" && window.location.pathname === "/login";
      const isAuthProbe = path.startsWith("/auth/") || path.includes("/auth/me");
      if (!onLogin && !isAuthProbe && typeof window !== "undefined") {
        window.location.href = "/login";
      }
      return Promise.reject(error);
    }
    const cfg = error.config || {};
    cfg.__retryCount = cfg.__retryCount || 0;
    if (cfg.__retryCount >= MAX_RETRIES || !isRetryable(error)) {
      setRetrying(false);
      return Promise.reject(error);
    }
    cfg.__retryCount += 1;
    setRetrying(true);
    const delay = BASE_DELAY_MS * 2 ** (cfg.__retryCount - 1);
    await sleep(delay);
    return api(cfg);
  },
);

export const extractCompanyPdf = (formData) =>
  api.post("/companies/extract-pdf", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });

export const parseCompanyExcel = (formData) =>
  api.post("/companies/parse-excel", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });

export const bulkCreateCompanies = (data) =>
  api.post("/companies/bulk-create", data);

export const excelTemplateUrl = () => `${API}/companies/excel-template`;

// Programmatic Excel-template download — works inside sandboxed iframes
// (the Emergent preview) where plain <a href> downloads get blocked.
export const downloadExcelTemplate = async () => {
  const res = await api.get("/companies/excel-template", { responseType: "blob" });
  const url = window.URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = "company_master_data_template.xlsx";
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

export const createCompany = (data) => api.post("/companies", data);
export const listCompanies = () => api.get("/companies");
export const getCompany = (id) => api.get(`/companies/${id}`);
export const updateCompany = (id, data) => api.patch(`/companies/${id}`, data);
export const deleteCompany = (id) => api.delete(`/companies/${id}`);
export const bulkDeleteCompanies = (ids) =>
  api.post("/companies/bulk-delete", { ids, cascade: true });

export const listResolutionTypes = () => api.get("/resolution-types");
export const generateResolution = (data) => api.post("/resolutions/generate", data);
export const listResolutions = (companyId) =>
  api.get("/resolutions", { params: companyId ? { company_id: companyId } : {} });
export const getResolution = (id) => api.get(`/resolutions/${id}`);
export const deleteResolution = (id) => api.delete(`/resolutions/${id}`);
export const lockResolution = (id, note = "") => api.post(`/resolutions/${id}/lock`, { note });
export const unlockResolution = (id) => api.post(`/resolutions/${id}/unlock`);
export const listResolutionAuditLog = (id) =>
  api.get(`/audit/log`, { params: { resource_type: "resolution", resource_id: id, limit: 50 } });

export const downloadResolutionPdfUrl = (id, docType = "resolution") =>
  `${API}/resolutions/${id}/pdf?doc_type=${docType}`;
export const downloadResolutionDocxUrl = (id, docType = "resolution") =>
  `${API}/resolutions/${id}/docx?doc_type=${docType}`;

// Compliance Calendar
export const seedCalendar = (companyId, numFys = 2) =>
  api.post(`/calendar/seed/${companyId}?num_fys=${numFys}`);
export const listCalendarEvents = (params = {}) => api.get("/calendar", { params });
export const createCalendarEvent = (data) => api.post("/calendar", data);
export const updateCalendarEvent = (id, data) => api.patch(`/calendar/${id}`, data);
export const deleteCalendarEvent = (id) => api.delete(`/calendar/${id}`);
export const markCalendarFiling = (id, data) => api.post(`/calendar/${id}/mark-filing`, data);
export const calendarStats = (companyId) =>
  api.get("/calendar/stats", { params: companyId ? { company_id: companyId } : {} });

// Procedures search
export const listProcedures = (params = {}) => api.get("/procedures", { params });
export const procedureFacets = () => api.get("/procedures/facets");
export const getProcedure = (key) => api.get(`/procedures/${key}`);

// Notifications (multi-source)
export const getNotifications = (params = {}) => api.get("/notifications", { params });
export const refreshNotifications = () => api.post("/notifications/refresh");
export const getNotificationArticle = (url) => api.get("/notifications/article", { params: { url } });
// Back-compat
export const getMcaFeed = (refresh = false) => api.get("/mca/feed", { params: { refresh } });

// AI Compliance Suggestions
export const suggestCompliance = (companyId, maxSuggestions = 6) =>
  api.post("/compliance/suggest", { company_id: companyId, max_suggestions: maxSuggestions });
export const applyComplianceSuggestions = (companyId, suggestions) =>
  api.post("/compliance/apply-suggestions", { company_id: companyId, suggestions });

// Bulk email to members
export const emailResolutionToMembers = (resolutionId) =>
  api.post(`/resolutions/${resolutionId}/email-members`);

// Style Library (RAG)
export const listStyleSamples = (params = {}) => api.get("/style-samples", { params });
export const uploadStyleSample = (formData) =>
  api.post("/style-samples/upload", formData, { headers: { "Content-Type": "multipart/form-data" } });
export const createStyleSample = (data) => api.post("/style-samples", data);
export const deleteStyleSample = (id) => api.delete(`/style-samples/${id}`);

// MCA Master-Data lookup (data.gov.in)
export const mcaLookupSearch = (q, limit = 10) =>
  api.get("/mca-lookup/search", { params: { q, limit } });
export const mcaLookupCin = (cin) =>
  api.get(`/mca-lookup/cin/${encodeURIComponent(cin)}`);

// Draft + Save batch (preview-then-save flow)
export const draftResolutionsBatch = (data) => api.post("/resolutions/draft", data);
export const saveResolutionsBatch = (data) => api.post("/resolutions/save-batch", data);

// Multi-company batch (same items applied to many companies in one shot)
export const multiCompanyBatch = (data) =>
  api.post("/resolutions/multi-batch", data);
export const downloadMultiZip = async (resolutionIds, opts = {}) => {
  const res = await api.post(
    "/resolutions/multi-zip",
    {
      resolution_ids: resolutionIds,
      include_notice: opts.includeNotice !== false,
      include_minutes: opts.includeMinutes !== false,
      formats: opts.formats || ["pdf", "docx"],
    },
    { responseType: "blob" },
  );
  const url = window.URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url;
  const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
  a.download = `ROC_Vault_Bulk_${ts}.zip`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

// FY Docket
export const getFyDocketTemplate = (companyId, fy) =>
  api.get(`/companies/${companyId}/fy-docket/template`, { params: fy ? { fy } : {} });

// Chat / Copilot
export const createChatSession = () => api.post("/chat/sessions");
export const listChatSessions = () => api.get("/chat/sessions");
export const getChatSession = (id) => api.get(`/chat/sessions/${id}`);
export const deleteChatSession = (id) => api.delete(`/chat/sessions/${id}`);
export const sendChatMessage = (sessionId, text) =>
  api.post(`/chat/sessions/${sessionId}/message`, { text });
export const executeChatAction = (sessionId, messageId) =>
  api.post(`/chat/sessions/${sessionId}/execute`, { message_id: messageId });

// Super-admin (platform owner)
export const superGetStats = () => api.get("/superadmin/stats");
export const superListFirms = () => api.get("/superadmin/firms");
export const superUpdateFirm = (firmId, payload) => api.patch(`/superadmin/firms/${firmId}`, payload);
export const superListUsers = () => api.get("/superadmin/users");
export const superGetPlans = () => api.get("/superadmin/plans");

// Meetings (groups of resolutions sharing meeting_id)
export const listMeetings = (companyId) =>
  api.get("/meetings", { params: companyId ? { company_id: companyId } : {} });
export const getMeeting = (id) => api.get(`/meetings/${id}`);

// Statutory Registers
export const listRegisters = (companyId) =>
  api.get(`/companies/${companyId}/registers`);
export const getRegister = (companyId, registerKey) =>
  api.get(`/companies/${companyId}/registers/${registerKey}`);
export const addRegisterEntry = (companyId, registerKey, payload) =>
  api.post(`/companies/${companyId}/registers/${registerKey}/entries`, payload);
export const updateRegisterEntry = (companyId, registerKey, entryId, payload) =>
  api.patch(`/companies/${companyId}/registers/${registerKey}/entries/${entryId}`, payload);
export const deleteRegisterEntry = (companyId, registerKey, entryId) =>
  api.delete(`/companies/${companyId}/registers/${registerKey}/entries/${entryId}`);
export const downloadRegisterFile = async (companyId, registerKey, fmt = "pdf") => {
  const res = await api.get(`/companies/${companyId}/registers/${registerKey}/${fmt}`, {
    responseType: "blob",
  });
  const url = window.URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${registerKey}.${fmt}`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

// Folio-mode register CRUD (MGT-1, MBP-2 Part I, MGT-2)
export const addFolio = (companyId, key, payload) =>
  api.post(`/companies/${companyId}/registers/${key}/folios`, payload);
export const updateFolio = (companyId, key, folioId, payload) =>
  api.patch(`/companies/${companyId}/registers/${key}/folios/${folioId}`, payload);
export const deleteFolio = (companyId, key, folioId) =>
  api.delete(`/companies/${companyId}/registers/${key}/folios/${folioId}`);
export const addTransaction = (companyId, key, folioId, payload) =>
  api.post(`/companies/${companyId}/registers/${key}/folios/${folioId}/transactions`, payload);
export const updateTransaction = (companyId, key, folioId, txnId, payload) =>
  api.patch(`/companies/${companyId}/registers/${key}/folios/${folioId}/transactions/${txnId}`, payload);
export const deleteTransaction = (companyId, key, folioId, txnId) =>
  api.delete(`/companies/${companyId}/registers/${key}/folios/${folioId}/transactions/${txnId}`);
export const downloadFolioPdf = async (companyId, key, folioId, label = "folio") => {
  const res = await api.get(`/companies/${companyId}/registers/${key}/folios/${folioId}/pdf`, {
    responseType: "blob",
  });
  const url = window.URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${label}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};
export const downloadFolioDocx = async (companyId, key, folioId, label = "folio") => {
  const res = await api.get(`/companies/${companyId}/registers/${key}/folios/${folioId}/docx`, {
    responseType: "blob",
  });
  const url = window.URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${label}.docx`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};
export const downloadSh1Pdf = async (companyId, payload) => {
  const res = await api.post(`/companies/${companyId}/forms/sh1/pdf`, payload, { responseType: "blob" });
  const url = window.URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = `SH-1_${payload.certificate_no || "certificate"}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};
export const downloadSh4Pdf = async (companyId, payload) => {
  const res = await api.post(`/companies/${companyId}/forms/sh4/pdf`, payload, { responseType: "blob" });
  const url = window.URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = `SH-4_${payload.instrument_no || "transfer"}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

// SH-4 → auto-link to MGT-1 + SH-6 registers (no PDF; companion call)
export const sh4AutoLink = (companyId, payload) =>
  api.post(`/companies/${companyId}/forms/sh4/autolink`, payload);

// --- MCA Form Filing Wizard ---------------------------------------------
export const getMcaWizard = (companyId, form, params = {}) =>
  api.get(`/companies/${companyId}/mca-forms/${form}/wizard`, { params });

export const listMcaDrafts = (companyId, form) =>
  api.get(`/companies/${companyId}/mca-forms/${form}/drafts`);

export const createMcaDraft = (companyId, form, payload) =>
  api.post(`/companies/${companyId}/mca-forms/${form}/drafts`, payload);

export const updateMcaDraft = (companyId, form, draftId, payload) =>
  api.patch(`/companies/${companyId}/mca-forms/${form}/drafts/${draftId}`, payload);

export const deleteMcaDraft = (companyId, form, draftId) =>
  api.delete(`/companies/${companyId}/mca-forms/${form}/drafts/${draftId}`);

export const downloadFilledMcaForm = async (companyId, form, payload) => {
  const res = await api.post(
    `/companies/${companyId}/mca-forms/${form}/filled-pdf`,
    payload,
    { responseType: "blob" },
  );
  const url = window.URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = `FILED_${form.toUpperCase()}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

export const getFilledMcaJson = (companyId, form, payload) =>
  api.post(`/companies/${companyId}/mca-forms/${form}/filled-json`, payload);

// Pre-AGM Compliance Pack — bundles every register PDF into one ZIP
export const downloadCompliancePack = async (companyId, fy) => {
  const url = fy ? `/companies/${companyId}/compliance-pack/pdf?fy=${encodeURIComponent(fy)}`
                 : `/companies/${companyId}/compliance-pack/pdf`;
  const res = await api.get(url, { responseType: "blob" });
  const blob = window.URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = blob;
  a.download = `compliance_pack${fy ? `_${fy}` : ""}.zip`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(blob);
};

// MCA V3 Prefill — downloads the Filing Cheat Sheet PDF
export const downloadMcaPrefill = async (companyId, form, fy) => {
  const params = new URLSearchParams();
  if (fy) params.set("fy", fy);
  const url = `/companies/${companyId}/mca-prefill/${form}${params.toString() ? `?${params}` : ""}`;
  const res = await api.get(url, { responseType: "blob" });
  const blob = window.URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = blob;
  a.download = `MCA_V3_${form.toUpperCase()}${fy ? `_${fy}` : ""}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(blob);
};

export const listMcaPrefillForms = () => api.get(`/mca-prefill/forms`);
export const getDscBook = () => api.get(`/dsc-book`);


// Document templates — incorporation forms, partnership deeds, audit reports, etc.
export const listDocumentTemplates = (category) =>
  api.get(`/document-templates`, { params: category ? { category } : {} });

export const generateDocumentTemplate = (templateKey, values, wantPdf = true) =>
  api.post(`/document-templates/${templateKey}/generate`, { values, want_pdf: wantPdf });

export const documentTemplateDownloadUrl = (templateKey, jobId, fmt) =>
  `${API}/document-templates/${templateKey}/download/${jobId}/${fmt}`;


// Email
export const emailStatus = () => api.get("/email/status");
export const emailResolution = (id, data) => api.post(`/resolutions/${id}/email`, data);
export const listEmailLogs = (id) => api.get(`/resolutions/${id}/email-logs`);
